import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Badge, InputGroup, Button, Spinner, Offcanvas, Alert } from 'react-bootstrap';
import { getBackendUrl } from '../config';
import { useAuth } from '../context/AuthContext';
import './SearchPage.scss';

const LEVELS = [
  { id: 'Anfänger', label: 'Anfänger' },
  { id: 'Fortgeschritten', label: 'Fortgeschritten' },
  { id: 'Profi', label: 'Profi' },
  { id: 'Experte', label: 'Experte' }
];

/**
 * SearchPage ist die Hauptsuchseite für Schüler.
 * Ermöglicht die Suche und Filterung aller registrierten Tutoren (Dozenten)
 * nach Namen, Fächern (Skills) und Niveaustufen in Echtzeit.
 */
export default function SearchPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [allTutors, setAllTutors] = useState([]);
  const [allSkills, setAllSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mainSearch, setMainSearch] = useState('');
  const [skillFilterSearch, setSkillFilterSearch] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  /**
   * Lädt alle globalen Skills und sucht parallel nach allen Tutoren,
   * indem typische Suchbegriffe parallel an den Such-Endpunkt gesendet werden.
   */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const sRes = await fetch(`${getBackendUrl()}/skill`, { headers: { 'Authorization': `Bearer ${token}` } });
        const skills = sRes.ok ? await sRes.json() : [];
        setAllSkills(skills);
        
        // Paralleles Abrufen aller Dozenten durch sichere Suchbegriffe
        const terms = ['%20', 'a', 'e', 'i', 'o', 'u'];
        const res = await Promise.all(terms.map(t => 
          fetch(`${getBackendUrl()}/suche/dozent/${t}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.ok ? r.json() : [])
        ));
        
        const unique = {};
        res.flat().forEach(t => t?.id && (unique[t.id] = t));
        setAllTutors(Object.values(unique));
      } catch (e) { 
        console.error("Fehler beim Laden der Dozenten:", e);
        setError("Ladefehler."); 
      } finally { 
        setLoading(false); 
      }
    })();
  }, [token]);

  /**
   * Filtert die Tutoren-Liste anhand der Suchbegriffe, Fächer und Niveaus.
   */
  const filtered = useMemo(() => allTutors.filter(t => 
    (t.vollerName?.toLowerCase().includes(mainSearch.toLowerCase()) || t.skills?.some(s => s.name?.toLowerCase().includes(mainSearch.toLowerCase()))) &&
    (!selectedSkills.length || t.skills?.some(s => selectedSkills.includes(s.name))) &&
    (!selectedLevels.length || t.skills?.some(s => selectedLevels.includes(s.niveau)))
  ), [allTutors, mainSearch, selectedSkills, selectedLevels]);

  /**
   * Filtert die in der Seitenleiste anzeigbaren Skills.
   */
  const visibleSkills = useMemo(() => allSkills.filter(s => 
    s.name && 
    !s.name.includes('-deleted') && 
    s.name.toLowerCase().includes(skillFilterSearch.toLowerCase())
  ), [allSkills, skillFilterSearch]);
  
  const toggle = (list, set, val) => set(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  return (
    <div className="search-page p-3 p-md-5">
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="page-header m-0">Dozenten finden</h1>
          <Button variant="outline-primary" className="d-lg-none rounded-pill" onClick={() => setShowFiltersMobile(true)}><i className="bi bi-filter me-2"></i>Filter</Button>
        </div>
        <Row>
          <Col lg={3} className="d-none d-lg-block">
            <div className="search-sidebar">
              <div className="mb-4">
                <h6 className="fw-bold mb-3">Level</h6>
                {LEVELS.map(l => <Form.Check key={l.id} type="checkbox" id={`l-${l.id}`} label={l.label} checked={selectedLevels.includes(l.id)} onChange={() => toggle(selectedLevels, setSelectedLevels, l.id)} className="mb-2 custom-checkbox" />)}
              </div>
              <div className="mb-4">
                <h6 className="fw-bold mb-3">Skills</h6>
                <InputGroup className="mb-3 skill-filter-search">
                  <InputGroup.Text className="bg-white border-0"><i className="bi bi-search small"></i></InputGroup.Text>
                  <Form.Control size="sm" placeholder="Suchen..." className="border-0 ps-0" value={skillFilterSearch} onChange={e => setSkillFilterSearch(e.target.value)} />
                </InputGroup>
                <div className="skill-list-scroll">
                  {visibleSkills.map(s => <Form.Check key={s.id} type="checkbox" id={`s-${s.id}`} label={s.name} checked={selectedSkills.includes(s.name)} onChange={() => toggle(selectedSkills, setSelectedSkills, s.name)} className="mb-2 custom-checkbox" />)}
                </div>
              </div>
              <Button variant="link" className="p-0 text-danger small text-decoration-none" onClick={() => { setSelectedSkills([]); setSelectedLevels([]); setMainSearch(''); }}>Reset</Button>
            </div>
          </Col>
          <Col lg={9}>
            <Card className="search-main-card border-0 shadow-sm mb-4">
              <Card.Body className="p-2"><InputGroup className="live-search-bar"><InputGroup.Text className="bg-transparent border-0 ps-4"><i className="bi bi-search text-muted"></i></InputGroup.Text><Form.Control placeholder="Live-Suche..." className="border-0 shadow-none py-3" value={mainSearch} onChange={e => setMainSearch(e.target.value)} /></InputGroup></Card.Body>
            </Card>
            {loading ? <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div> : error ? <Alert variant="danger">{error}</Alert> : (
              <>
                <div className="mb-3 d-flex align-items-center flex-wrap gap-2">
                  <span className="fw-bold">{filtered.length} Dozenten</span>
                  {selectedSkills.map(s => <Badge key={s} pill className="results-badge-custom py-1 px-3">{s} <i className="bi bi-x ms-1" onClick={() => toggle(selectedSkills, setSelectedSkills, s)}></i></Badge>)}
                </div>
                <Row xs={1} md={2} className="g-4">
                  {filtered.map(t => {
                    const price = t.skills?.length ? Math.min(...t.skills.filter(s => s.stundenlohn).map(s => s.stundenlohn)) : null;
                    return (
                      <Col key={t.id}>
                        <Card className="tutor-glass-card border-0 shadow-sm h-100" onClick={() => navigate(`/tutor/${t.id}`, { state: { tutor: t } })}>
                          <Card.Body className="p-4">
                            <div className="d-flex align-items-center mb-3">
                              <div className="tutor-avatar-circle me-3">{t.vollerName?.[0] || 'T'}</div>
                              <div>
                                <h5 className="mb-0 fw-bold">{t.vollerName}</h5>
                                <div className="small text-muted">{t.email}</div>
                              </div>
                            </div>
                             <div className="mb-3">{t.skills?.filter(s => s.name && !s.name.includes('-deleted')).slice(0, 3).map(s => <Badge key={s.id} className="skill-tag-badge me-2 mb-2 border-0">{s.name} • {s.niveau}</Badge>)}</div>
                            <p className="small text-muted mb-0">{t.bio || 'Keine Bio.'}</p>
                          </Card.Body>
                          <Card.Footer className="bg-transparent border-0 p-4 pt-0 d-flex justify-content-between align-items-center">
                            <div><span className="price-amount h5">{price ? `ab ${price}€` : '–'}</span><span className="small text-muted ms-1">/ Std.</span></div>
                            <Button variant="link" className="p-0 text-decoration-none details-link">Details &rarr;</Button>
                          </Card.Footer>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
                {!filtered.length && <div className="text-center py-5 text-muted"><i className="bi bi-search display-1 opacity-25"></i><p>Keine Treffer.</p></div>}
              </>
            )}
          </Col>
        </Row>
      </Container>
      <Offcanvas show={showFiltersMobile} onHide={() => setShowFiltersMobile(false)} placement="end">
        <Offcanvas.Header closeButton><Offcanvas.Title>Filter</Offcanvas.Title></Offcanvas.Header>
        <Offcanvas.Body>
          <div className="search-sidebar">
            <div className="mb-4">
              <h6 className="fw-bold mb-3">Level</h6>
              {LEVELS.map(l => <Form.Check key={l.id} type="checkbox" id={`m-l-${l.id}`} label={l.label} checked={selectedLevels.includes(l.id)} onChange={() => toggle(selectedLevels, setSelectedLevels, l.id)} className="mb-2 custom-checkbox" />)}
            </div>
            <div className="mb-4">
              <h6 className="fw-bold mb-3">Skills</h6>
              <InputGroup className="mb-3 skill-filter-search">
                <InputGroup.Text className="bg-white border-0"><i className="bi bi-search small"></i></InputGroup.Text>
                <Form.Control size="sm" placeholder="Suchen..." className="border-0 ps-0" value={skillFilterSearch} onChange={e => setSkillFilterSearch(e.target.value)} />
              </InputGroup>
              <div className="skill-list-scroll">
                {visibleSkills.map(s => <Form.Check key={s.id} type="checkbox" id={`m-s-${s.id}`} label={s.name} checked={selectedSkills.includes(s.name)} onChange={() => toggle(selectedSkills, setSelectedSkills, s.name)} className="mb-2 custom-checkbox" />)}
              </div>
            </div>
            <Button variant="link" className="p-0 text-danger small text-decoration-none" onClick={() => { setSelectedSkills([]); setSelectedLevels([]); setMainSearch(''); }}>Reset</Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
